import { Box, Grid, Menu, TextField } from '@mui/material';
import React, { useCallback, useState } from 'react';
import CustomTypography from '../../../../components/mui/CustomTypography';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import { secondsToTime, timeToSeconds } from '../../../../utils/secondsToTime';
import { CustomButton } from '../../../../components/mui/CustomButton';
import { trackEvent } from '../../../../utils/ga';

const TimeMenu = React.memo(({ time, open, anchorEl, onChange, onClose }) => {
  const [value, setValue] = useState(() => {
    const [hours, minutes, seconds] = time.split(':').map((t) => t.padStart(2, '0'));
    return { hours, minutes, seconds };
  });

  const handleChange = useCallback(
    (field) => (event) => {
      let inputValue = event.target.value.replace(/\D/g, ''); // Remove non-numeric characters

      if (field === 'hours') {
        inputValue = inputValue.slice(0, 2); // Limit to 2 digits
      } else {
        inputValue = inputValue > 59 ? '59' : inputValue;
      }

      setValue((prev) => ({ ...prev, [field]: inputValue }));
    },
    [],
  );

  const handleUpdate = useCallback(() => {
    const totalSeconds = timeToSeconds(`${value.hours}:${value.minutes}:${value.seconds}`);
    trackEvent('time_update_from_transcript', {
      time_changed: `${time} --> ${value.hours}:${value.minutes}:${value.seconds}`,
    });
    onChange(totalSeconds);
    onClose();
  }, [value, onChange, onClose]);

  return (
    <Menu
      open={open}
      onClose={() => {
        const [hours, minutes, seconds] = time.split(':').map((t) => t.padStart(2, '0'));
        setValue({ hours, minutes, seconds });
        onClose();
      }}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      sx={{
        '& .MuiPaper-root': {
          maxHeight: '380px',
          width: '320px',
          background: '#ffffff',
          boxShadow: '0px 4px 4px rgb(0 0 0 / 25%)',
          border: '1px solid #ebebeb',
          borderRadius: '4px',
          position: 'absolute',
          zIndex: 1,
          overflowY: 'hidden',
        },
      }}
    >
      <div className="speaktoedit-header" tabIndex={null}>
        <h6 className="fnt-16 font-weight-bold mb-0">Time</h6>
        <div id="closedSpeakerModel" onClick={onClose}>
          <HighlightOffRoundedIcon
            id="closedSpeakerModel"
            className="cursor-pointer opacity-50"
            style={{ height: '28px', width: '28px', marginLeft: '5px' }}
          />
        </div>
      </div>
      <Box className="speaktoedit-body" sx={{ marginBottom: 2 }}>
        <Grid container spacing={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Grid item xs={3.5} sx={{ paddingLeft: '0 !important' }}>
            <TextField
              label="HH"
              value={value.hours}
              onChange={handleChange('hours')}
              onBlur={() => {
                if (!value.hours) {
                  setValue((prev) => ({ ...prev, hours: '00' }));
                }
              }}
              inputProps={{ maxLength: 2 }}
              fullWidth
            />
          </Grid>
          <CustomTypography>:</CustomTypography>
          <Grid item xs={3.5} sx={{ paddingLeft: '0 !important' }}>
            <TextField
              label="mm"
              value={value.minutes}
              onChange={handleChange('minutes')}
              onBlur={() => {
                if (!value.minutes) {
                  setValue((prev) => ({ ...prev, minutes: '00' }));
                }
              }}
              inputProps={{ maxLength: 2 }}
              fullWidth
            />
          </Grid>
          <CustomTypography>:</CustomTypography>
          <Grid item xs={3.5} sx={{ paddingLeft: '0 !important' }}>
            <TextField
              label="ss"
              value={value.seconds}
              onChange={handleChange('seconds')}
              onBlur={() => {
                if (!value.seconds) {
                  setValue((prev) => ({ ...prev, seconds: '00' }));
                }
              }}
              inputProps={{ maxLength: 2 }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
      <div className="speaktoedit-footer" style={{ justifyContent: 'end' }}>
        <CustomButton variant="secondary" id="createSpeakerLabel" onClick={handleUpdate}>
          Update
        </CustomButton>
      </div>
    </Menu>
  );
});

const SlateTimeLabel = ({ fontSize, startTime, onChange, allowEdit }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = useCallback((event) => {
    event?.stopPropagation()
    if (!allowEdit) return;
    setAnchorEl(event.currentTarget);
  }, []);
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);
  return (
    <Box sx={{ position: 'relative' }} contentEditable={false}>
      <CustomTypography
        contentEditable={false}
        size="smallMedium"
        variant="p"
        sx={{
          fontSize: `${Math.max(fontSize, 10)}px !important`,
          color: '#190041 !important',
          textAlign: 'left',
          cursor: allowEdit ? 'pointer' : 'default',
          userSelect: 'none'
        }}
        onClick={handleClick}
      >
        {secondsToTime(startTime)}
      </CustomTypography>
      <TimeMenu
        time={secondsToTime(startTime)}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onChange={onChange}
        onClose={handleClose}
      />
    </Box>
  );
};

export default React.memo(SlateTimeLabel);
